import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Configure axios to ALWAYS send cookies with requests
// This MUST be set before any axios call
axios.defaults.withCredentials = true;

// Also add an interceptor to ensure withCredentials on every request
axios.interceptors.request.use(
  (config) => {
    config.withCredentials = true;
    return config;
  },
  (error) => Promise.reject(error)
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAccountBlocked, setIsAccountBlocked] = useState(false);
  // Master plan multi-company support
  const [isMaster, setIsMaster] = useState(false);
  const [managedCompanies, setManagedCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);

  // Setup axios interceptor to catch blocked account errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403 && error.response?.data?.detail === 'ACCOUNT_BLOCKED') {
          setIsAccountBlocked(true);
          // Redirect to blocked page
          if (window.location.pathname !== '/conta-bloqueada') {
            window.location.href = '/conta-bloqueada';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    // Check if user is authenticated via cookie or localStorage (fallback)
    const token = localStorage.getItem('token');
    if (token) {
      // Keep header for backwards compatibility during migration
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    // Always try to fetch user - cookie will authenticate if present
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data.user);
      setCompany(response.data.company);
      
      // Check if user has Master plan
      const subscriptionPlan = response.data.company?.subscription_plan;
      if (subscriptionPlan === 'master') {
        setIsMaster(true);
        // Fetch managed companies
        await fetchManagedCompanies();
      } else {
        setIsMaster(false);
        setManagedCompanies([]);
      }
      
      // Set active company (from user preferences or default to main company)
      const savedActiveCompanyId = localStorage.getItem('activeCompanyId');
      if (savedActiveCompanyId && subscriptionPlan === 'master') {
        // Will be validated in fetchManagedCompanies
        setActiveCompany(response.data.company);
      } else {
        setActiveCompany(response.data.company);
      }
    } catch (error) {
      // User not authenticated - clear any stale data
      localStorage.removeItem('token');
      localStorage.removeItem('activeCompanyId');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const fetchManagedCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/master/companies`);
      const companies = response.data.companies || [];
      setManagedCompanies(companies);
      
      // If user had a saved active company, validate and set it
      const savedActiveCompanyId = localStorage.getItem('activeCompanyId');
      if (savedActiveCompanyId) {
        const savedCompany = companies.find(c => c.id === savedActiveCompanyId);
        if (savedCompany) {
          setActiveCompany(savedCompany);
        }
      }
    } catch (error) {
      setManagedCompanies([]);
    }
  };

  const switchCompany = async (companyId) => {
    try {
      const response = await axios.post(`${API_URL}/master/switch-company/${companyId}`);
      if (response.data.success) {
        const newActiveCompany = response.data.active_company;
        setActiveCompany(newActiveCompany);
        localStorage.setItem('activeCompanyId', companyId);
        
        // Dispatch a custom event to notify all components to refresh their data
        window.dispatchEvent(new CustomEvent('companyChanged', { 
          detail: { companyId, company: newActiveCompany } 
        }));
        
        // Refresh user data without full page reload
        await fetchUser();
        await fetchManagedCompanies();
      }
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token, user: userData } = response.data;
    // Keep localStorage as fallback for backwards compatibility
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    await fetchUser();
    // Return user data for redirect logic
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    const { token, user: userData } = response.data;
    // Keep localStorage as fallback for backwards compatibility
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    await fetchUser();
  };

  const logout = async () => {
    try {
      // Call backend to clear httpOnly cookie
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      // Continue with local cleanup even if API call fails
    }
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('activeCompanyId');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setCompany(null);
    setIsMaster(false);
    setManagedCompanies([]);
    setActiveCompany(null);
    setIsAccountBlocked(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      company, 
      loading, 
      login, 
      register, 
      logout,
      isAccountBlocked,
      // Master plan features
      isMaster,
      managedCompanies,
      activeCompany,
      switchCompany,
      refreshCompanies: fetchManagedCompanies
    }}>
      {children}
    </AuthContext.Provider>
  );
};
